import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Skeleton,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { AuthActionTypes, getUser, useAuth } from '@devd-client/devd/auth';
import {
  DateFilter,
  DownloadButton,
  durationData,
  ISelect,
  Loader,
  Paginate,
  PeriodType,
  TeamMenu,
  useDashboardStore,
  useToastHook,
} from '@devd-client/devd/components';
import moment from 'moment';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { downloadProductStatsReport } from '../../api/endpoints';
import { useProductStatsGql } from '../../graphql';
import { ProductStatsItem } from '../../helpers/report.types';
import { formatNumber } from '../../helpers/report.utils';
import { EngineersCell } from './ProductStatsCol';
import { normalizeEngineersList, truncateProductName } from './utils';

interface ProductStatsField {
  field: 'ticketCount' | 'totalEffort' | 'engineers';
  name: string;
  numeric?: boolean;
}

const PRODUCT_STATS_FIELDS: ProductStatsField[] = [
  { field: 'ticketCount', name: 'Ticket Count', numeric: true },
  { field: 'totalEffort', name: 'Total Effort', numeric: true },
  { field: 'engineers', name: 'Engineers' },
];

const PAGE_SIZE = 10;

/** Single grid so row heights stay aligned when Engineers cells expand. */
const GRID_TEMPLATE_COLUMNS =
  'minmax(260px, 280px) minmax(120px, 140px) minmax(160px, 180px) minmax(160px, 180px) minmax(360px, 560px)';

const HEADER_BG = '#FBFBFB';
const BORDER = '1px solid #E0E0E0';

const renderNumeric = (
  value: ProductStatsItem['ticketCount'] | ProductStatsItem['totalEffort'],
  numeric?: boolean
) => {
  if (value === null || value === undefined) return '-';
  if (numeric) return formatNumber(value);
  return String(value);
};

export const ProductStatsTable = () => {
  const { selected, setSelected } = useDashboardStore();
  const [appState, dispatch] = useAuth();
  const [newToast] = useToastHook();
  const [downloadPending, setDownloadPending] = useState(false);

  useEffect(() => {
    if (selected.startDate && selected.endDate) return;
    const sprintPref = appState?.user?.dto?.sprintDetail;
    if (appState?.user?.dto?.sprint && sprintPref) {
      try {
        const parsed = JSON.parse(sprintPref);
        setSelected({
          ...(durationData[6] as PeriodType),
          startDate: moment(parsed?.startDate).format('YYYY-MM-DD'),
          endDate: moment(parsed?.endDate).format('YYYY-MM-DD'),
          sprintId: appState.user.dto.sprint,
          sprintName: parsed?.sprintName,
        });
        return;
      } catch {
        // fall through
      }
    }
    setSelected(durationData[3] as PeriodType);
  }, []);

  const [selectedTeam, setSelectedTeam] = useState<ISelect>({
    value: appState?.user?.dto?.team || '',
    label: appState?.user?.dto?.team || 'Organisation',
  });
  const [currentPage, setCurrentPage] = useState(0);

  const teamIds = useMemo(
    () =>
      selectedTeam.value && selectedTeam.label !== 'Organisation'
        ? [selectedTeam.value]
        : [],
    [selectedTeam]
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [selected.startDate, selected.endDate, selected.sprintId, teamIds]);

  const { data, isFetching } = useProductStatsGql(
    {
      startDate: selected.startDate,
      endDate: selected.endDate,
      teamIds,
      sprintId: selected.sprintId || '',
    },
    currentPage,
    PAGE_SIZE
  );

  const rows: ProductStatsItem[] = useMemo(
    () => data?.data ?? [],
    [data?.data]
  );

  const rowMinH = '110px';

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page - 1);
  }, []);

  const handleDownload = useCallback(async () => {
    if (!selected.startDate || !selected.endDate) return;

    setDownloadPending(true);
    try {
      const res = await downloadProductStatsReport({
        startDate: selected.startDate,
        endDate: selected.endDate,
        teamIds,
        sprintId: selected.sprintId || '',
      });
      newToast({
        message:
          res.dto ||
          'Your request has been received. The product stats xlsx will be sent to your email shortly.',
        status: 'success',
      });
    } catch (err: unknown) {
      const message =
        (err as { message?: string })?.message ||
        'There is some error in downloading data. Please try again or contact support at support@devdynamics.ai';
      newToast({
        message,
        status: 'error',
      });
    } finally {
      setDownloadPending(false);
    }
  }, [
    newToast,
    selected.endDate,
    selected.sprintId,
    selected.startDate,
    teamIds,
  ]);

  const lastFieldName =
    PRODUCT_STATS_FIELDS[PRODUCT_STATS_FIELDS.length - 1]?.name;

  return (
    <Flex direction="column" w="100%">
      <Flex align="center" justify="space-between">
        <Heading as="h1" size="md">
          Product Report
        </Heading>
        <Flex align="center" gap={3}>
          <DownloadButton downloadCSV={handleDownload} />
          <DateFilter
            getUser={getUser}
            appState={appState}
            dispatch={dispatch}
            AuthActionTypes={AuthActionTypes}
          />
        </Flex>
      </Flex>

      <Flex align="center" mt={4}>
        <TeamMenu
          selectedTeam={selectedTeam}
          handleSelectedTeam={setSelectedTeam}
          AuthActionTypes={AuthActionTypes}
          getUser={getUser}
          appState={appState}
          dispatch={dispatch}
          hideShowDetails
        />
      </Flex>

      <Box
        mt={3}
        overflowX="auto"
        overflowY="auto"
        border={BORDER}
        height="66vh"
        width="100%"
        borderRadius="8px"
        position="relative"
        bg="white"
      >
        <Grid
          templateColumns={GRID_TEMPLATE_COLUMNS}
          w="max-content"
          minW="100%"
        >
          {/* Header row */}
          <GridItem
            position="sticky"
            top={0}
            left={0}
            zIndex={21}
            bg={HEADER_BG}
            minH="52px"
            display="flex"
            alignItems="center"
            px={4}
            py={2}
            borderBottom={BORDER}
            boxShadow="1px 0 0 0 #E0E0E0"
          >
            <Text
              fontSize="sm"
              fontFamily="heading"
              fontWeight="medium"
              color="text.secondary2"
            >
              Product
            </Text>
          </GridItem>
          <GridItem
            position="sticky"
            top={0}
            zIndex={20}
            bg={HEADER_BG}
            minH="52px"
            display="flex"
            alignItems="center"
            px={4}
            borderLeft={BORDER}
            borderBottom={BORDER}
          >
            <Tooltip label="Number of engineers on this product" hasArrow>
              <Text
                fontSize="sm"
                fontFamily="heading"
                fontWeight="medium"
                color="text.secondary2"
                noOfLines={1}
              >
                Engineer count
              </Text>
            </Tooltip>
          </GridItem>
          {PRODUCT_STATS_FIELDS.map((f) => (
            <GridItem
              key={`h-${f.field}`}
              position="sticky"
              top={0}
              zIndex={20}
              bg={HEADER_BG}
              minH="52px"
              display="flex"
              alignItems="center"
              px={4}
              borderLeft={BORDER}
              borderBottom={BORDER}
              borderRight={f.name === lastFieldName ? BORDER : undefined}
            >
              <Tooltip label={f.name} hasArrow isDisabled={f.name.length < 22}>
                <Text
                  fontSize="sm"
                  fontFamily="heading"
                  fontWeight="medium"
                  color="text.secondary2"
                  noOfLines={1}
                >
                  {f.name}
                </Text>
              </Tooltip>
            </GridItem>
          ))}

          {isFetching
            ? [...Array(10)].map((_, rowIndex) => (
                <Fragment key={`sk-${rowIndex}`}>
                  <GridItem
                    position="sticky"
                    left={0}
                    zIndex={10}
                    bg="white"
                    minH={rowMinH}
                    display="flex"
                    alignItems="flex-start"
                    p={3}
                    pl={4}
                    borderBottom={BORDER}
                    boxShadow="1px 0 0 0 #E0E0E0"
                  >
                    <Skeleton height="14px" width="200px" />
                  </GridItem>
                  <GridItem
                    minH={rowMinH}
                    display="flex"
                    alignItems="flex-start"
                    px={4}
                    py={3}
                    borderLeft={BORDER}
                    borderBottom={BORDER}
                  >
                    <Skeleton height="14px" width="32px" />
                  </GridItem>
                  <GridItem
                    minH={rowMinH}
                    display="flex"
                    alignItems="flex-start"
                    px={4}
                    py={3}
                    borderLeft={BORDER}
                    borderBottom={BORDER}
                  >
                    <Skeleton height="14px" width="60px" />
                  </GridItem>
                  <GridItem
                    minH={rowMinH}
                    display="flex"
                    alignItems="flex-start"
                    px={4}
                    py={3}
                    borderLeft={BORDER}
                    borderBottom={BORDER}
                  >
                    <Skeleton height="14px" width="60px" />
                  </GridItem>
                  <GridItem
                    minH={rowMinH}
                    display="flex"
                    alignItems="flex-start"
                    px={4}
                    py={3}
                    borderLeft={BORDER}
                    borderRight={BORDER}
                    borderBottom={BORDER}
                  >
                    <Wrap spacing={1.5} w="full">
                      {[1, 2, 3].map((k) => (
                        <WrapItem key={k}>
                          <Skeleton
                            height="22px"
                            width="100px"
                            borderRadius="md"
                          />
                        </WrapItem>
                      ))}
                    </Wrap>
                  </GridItem>
                </Fragment>
              ))
            : rows.map((row) => {
                const name = row.productName || '—';
                const count = normalizeEngineersList(row.engineers).length;
                return (
                  <Fragment key={row.productId}>
                    <GridItem
                      position="sticky"
                      left={0}
                      zIndex={10}
                      bg="white"
                      minH={rowMinH}
                      display="flex"
                      alignItems="flex-start"
                      p={3}
                      pl={4}
                      borderBottom={BORDER}
                      boxShadow="1px 0 0 0 #E0E0E0"
                    >
                      <Tooltip
                        label={name}
                        hasArrow
                        isDisabled={!name || name.length < 36}
                      >
                        <Text
                          fontSize="sm"
                          fontFamily="heading"
                          color="text.primary"
                          fontWeight="medium"
                        >
                          {truncateProductName(name, 36)}
                        </Text>
                      </Tooltip>
                    </GridItem>
                    <GridItem
                      minH={rowMinH}
                      display="flex"
                      alignItems="flex-start"
                      px={4}
                      py={3}
                      borderLeft={BORDER}
                      borderBottom={BORDER}
                    >
                      <Text
                        fontSize="sm"
                        fontFamily="heading"
                        color="text.primary"
                        fontWeight="normal"
                      >
                        {count}
                      </Text>
                    </GridItem>
                    <GridItem
                      minH={rowMinH}
                      display="flex"
                      alignItems="flex-start"
                      px={4}
                      py={3}
                      borderLeft={BORDER}
                      borderBottom={BORDER}
                    >
                      <Text
                        fontSize="sm"
                        fontFamily="heading"
                        color="text.primary"
                        fontWeight="normal"
                        noOfLines={2}
                        wordBreak="break-word"
                      >
                        {renderNumeric(row.ticketCount, true)}
                      </Text>
                    </GridItem>
                    <GridItem
                      minH={rowMinH}
                      display="flex"
                      alignItems="flex-start"
                      px={4}
                      py={3}
                      borderLeft={BORDER}
                      borderBottom={BORDER}
                    >
                      <Text
                        fontSize="sm"
                        fontFamily="heading"
                        color="text.primary"
                        fontWeight="normal"
                        noOfLines={2}
                        wordBreak="break-word"
                      >
                        {renderNumeric(row.totalEffort, true)}
                      </Text>
                    </GridItem>
                    <GridItem
                      minH={rowMinH}
                      display="flex"
                      alignItems="flex-start"
                      px={4}
                      py={3}
                      borderLeft={BORDER}
                      borderRight={BORDER}
                      borderBottom={BORDER}
                    >
                      <EngineersCell value={row.engineers} />
                    </GridItem>
                  </Fragment>
                );
              })}
        </Grid>
      </Box>

      <Box marginLeft="-20px">
        <Paginate
          currentPage={(data?.currentPage ?? 0) + 1}
          totalPageCount={data?.totalPages ?? 1}
          pageSize={PAGE_SIZE}
          onPageChange={(page) => handlePageChange(page as number)}
        />
      </Box>

      {downloadPending && <Loader />}
    </Flex>
  );
};

export default ProductStatsTable;
